import {ITopCategory} from "../../interfaces/ITopCategory";
import {request} from "../../shared/httpInterceptor";
import {useCountryLang} from "../../hooks/useCountryLang";
import {useQuery} from "@tanstack/react-query";
import {Offcanvas} from "react-bootstrap";
import Button from "../Button";
import Icon from "../Icon";
import Col from "react-bootstrap/Col";
import ImageWithFallback from "../ImageWithFallback";
import React, {useEffect, useState} from "react";
import {useRouter} from "next/router";
import styled from "styled-components";
import {useMessages} from "../../hooks/useMessages";
import MenuSubcategoryPlaceholder from "../placeholders/category/MenuSubcategoryPlaceholder";
import {getTheme} from "../../shared/themeUtils";

const Contain = styled.div`
  .menu-container {
    position: relative;
  }

  .menu {
    background: yellow;
    overflow-y: scroll;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    transition: transform 0.3s ease-in-out;
  }

  .menu.slide-out {
    transform: translateX(-100%);
  }
`

const Element = styled.li`
  list-style: none;
  border-bottom: 1px solid ${getTheme("colors.border")};
  margin-right: ${getTheme("space.2")};
  padding: ${getTheme("space.2")} ${getTheme("space.1")};
  font-size: ${getTheme("fontSizes.medium")};
  display: flex;
  font-weight: 500;
  cursor: pointer;

  .image-contain {
    margin-right: ${getTheme("space.1")};
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    width: 60px;
    height: 60px;
  }

  .text-contain {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0;
    margin-left: ${getTheme("space.1")};
  }

    .navigate-subcat-btn {
        padding: 0;
        margin-left: ${getTheme("space.0")};
        height: auto;
        .direction-icon {
            background-color: ${getTheme("colors.background")};
            padding: ${getTheme("space.0")};
        }
    }
`

const fetchSubcategories = async (countryLang: string | null, categoryId: number) => {
  const res = await request({
    url: `/${countryLang}/category/tree/${categoryId}?childInfo=true`,
    method: 'GET'
  })
  return res.data
}

type MenuSubcategoryProps = {
  category: ITopCategory,
}

const MenuSubcategory = (props: MenuSubcategoryProps) => {
  const countryLang = useCountryLang()
  const router = useRouter()
  const [selectedSubcategory, setSelectedSubcategory] = useState<ITopCategory | null>(null)
  const [showSubCats, setShowSubCats] = useState(false)
  const messagesQuery = useMessages()

  const subcategoriesQuery = useQuery<ITopCategory[]>(["subcategories", props.category.id, countryLang],
    () => fetchSubcategories(countryLang, props.category.id), {
    enabled: countryLang!=null
    })


  useEffect(() => {
    setShowSubCats(true)
    return () => {
      handleSubCatsClose()
    }
  }, [props.category])


  const handleSubCatsShow = async (category: ITopCategory) => {
    if (category.hasSubCategories) {
      setSelectedSubcategory(category)
      setShowSubCats(true)
    }
  }


  const navigateToCategory = async (category: ITopCategory) => {
    if (category.customSlug) {
      await router.push(`/${countryLang}/custom/${category.customSlug}`)
    } else {
      await router.push(`/${countryLang}/cat/${category.slug}`)
    }
  }


  const handleSubCatsClose = () => {
    setSelectedSubcategory(null)
    setShowSubCats(false)
  }


  return (
    <Contain>
      <Offcanvas show={showSubCats}
                 onHide={handleSubCatsClose}
                 backdrop={false}
                 className="subcats-offcanvas">
        <Offcanvas.Header className={"pb-0"}>
          <Offcanvas.Title className={"w-100"}>
            <Button variant={"transparent"}
                    className={"d-flex align-items-center border-bottom-0"}
                    onClick={handleSubCatsClose}>
              <Icon className="direction-icon"
                    variant="medium"
                    color={"primary"}>
                arrow-left-line
              </Icon>
              {props.category.name}
            </Button>
            <hr className={"opacity-25 mb-0"}/>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {subcategoriesQuery.isLoading &&
            <>
              <MenuSubcategoryPlaceholder/>
              <MenuSubcategoryPlaceholder/>
              <MenuSubcategoryPlaceholder/>
              <MenuSubcategoryPlaceholder/>
              <MenuSubcategoryPlaceholder/>
              <MenuSubcategoryPlaceholder/>
              <MenuSubcategoryPlaceholder/>
              <MenuSubcategoryPlaceholder/>
              <MenuSubcategoryPlaceholder/>
              <MenuSubcategoryPlaceholder/>
            </>
          }

          {subcategoriesQuery.data && subcategoriesQuery.data.map(sub => {
            return (
              <Element key={sub.id}>
                <Col xs="2"
                     className="image-contain"
                     onClick={() => navigateToCategory(sub)}>
                  {sub.imageURL &&
                    <ImageWithFallback key={sub.id}
                                       alt={sub.name}
                                       src={sub.imageURL}
                                       fallbackSrc={'/client-images/box-flat.png'}
                                       layout="fill"
                                       objectFit={"contain"}/>}
                </Col>
                <Col className="text-contain" onClick={() => navigateToCategory(sub)}>
                  <span>{sub.name}</span>
                </Col>
                {sub.hasSubCategories &&
                  <Button onClick={() => handleSubCatsShow(sub)}
                          variant={"transparent"}
                          className={"navigate-subcat-btn"}>
                    <Icon className="direction-icon"
                          variant="small">
                      arrow-right-line
                    </Icon>
                  </Button>
                }
              </Element>
            )
          })}
        </Offcanvas.Body>
      </Offcanvas>

      {selectedSubcategory && <MenuSubcategory category={selectedSubcategory} />}

    </Contain>
  )
}

export default MenuSubcategory